import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { GetAdminCopilotMaterialsCall } from "../../redux/api/lectureLab.api";
import AdminStatsStudyPlans from "../../prof/stats/AdminStatsStudyPlans";

const headCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.secondary",
  borderBottom: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
};

const bodyCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.875rem",
  verticalAlign: "middle",
  borderColor: "divider",
};

const SOURCE_TYPE_LABEL = {
  pdf: "PDF",
  ppt: "PowerPoint",
  image: "Image",
  audio: "Audio",
};

const STATUS_LABEL = {
  queued: "Queued",
  processing: "Processing",
  ready: "Ready",
  failed: "Failed",
};

function statusChipColor(status) {
  switch (status) {
    case "ready":
      return "success";
    case "processing":
      return "info";
    case "queued":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

function sourceTypeChipColor(sourceType) {
  switch (sourceType) {
    case "pdf":
      return "error";
    case "ppt":
      return "warning";
    case "image":
      return "info";
    case "audio":
      return "secondary";
    default:
      return "default";
  }
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function initialsFor(user) {
  if (!user) return "?";
  const first = user.firstName?.charAt(0) || "";
  const last = user.lastName?.charAt(0) || "";
  const combo = `${first}${last}`.toUpperCase();
  if (combo) return combo;
  if (user.email) return user.email.charAt(0).toUpperCase();
  return "?";
}

export default function CopilotUsagePage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  /*
    Pagination state lives here, NOT in a `{ page, limit, total, ... }`
    blob, so the data-fetching effect can list exactly what it depends
    on and avoid the duplicate-fetch trap. Pattern:
      - page / limit  drive the request                 (user-controlled)
      - meta          carries server-reported totals    (server-derived)
    Filters reset page back to 1 in a separate effect.
  */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  // Both filters are server-side; empty string means "all".
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [section, setSection] = useState("copilot");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GetAdminCopilotMaterialsCall({
      page,
      limit,
      status: statusFilter || null,
      sourceType: sourceFilter || null,
    }).then(({ materials, pagination: serverMeta }) => {
      if (cancelled) return;
      setRows(Array.isArray(materials) ? materials : []);
      setMeta({
        total: serverMeta?.total || 0,
        totalPages: serverMeta?.totalPages || 1,
      });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [page, limit, statusFilter, sourceFilter, refreshTick]);

  /*
    Whenever a filter changes, snap back to page 1. Decoupling this from
    the fetch effect keeps each effect's dependency list honest and
    avoids the "filters changed but we're still on page 7" edge case.
  */
  useEffect(() => {
    setPage(1);
  }, [statusFilter, sourceFilter]);

  const handlePageChange = (_event, newZeroBasedPage) => {
    setPage(newZeroBasedPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10) || 25;
    setLimit(newLimit);
    setPage(1);
  };

  const subtitle = useMemo(() => {
    if (loading) return "Loading…";
    if (meta.total === 0) return "No Copilot uploads yet.";
    if (meta.total === 1) return "1 Copilot upload";
    return `${meta.total} Copilot uploads`;
  }, [loading, meta.total]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "flex-end" }}
            justifyContent="space-between"
          >
            <Box>
              <MDTypography
                variant="h4"
                fontWeight="bold"
                color="text"
                sx={{ letterSpacing: "-0.02em" }}
              >
                Copilot usage
              </MDTypography>
              <MDTypography
                variant="body1"
                color="text"
                sx={{ mt: 1, maxWidth: 760, lineHeight: 1.6, opacity: 0.85 }}
              >
                {section === "study"
                  ? "Study plans people create in the app: who made them, the subject, progress, and the date and time they were created and last used."
                  : "Every PDF, PowerPoint, image or audio file users upload to the Microlab Copilot panel. Latest first. Click view to inspect the generated summary, key structures, quiz and flashcards exactly as the user sees them in the app."}
              </MDTypography>
              {section === "copilot" ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>

            {section === "study" ? null : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="copilot-source-filter">Source type</InputLabel>
                <Select
                  labelId="copilot-source-filter"
                  label="Source type"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">All sources</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="ppt">PowerPoint</MenuItem>
                  <MenuItem value="image">Image</MenuItem>
                  <MenuItem value="audio">Audio</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="copilot-status-filter">Status</InputLabel>
                <Select
                  labelId="copilot-status-filter"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="queued">Queued</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => setRefreshTick((t) => t + 1)}
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            )}
          </Stack>

          <Tabs
            value={section}
            onChange={(_event, value) => setSection(value)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab value="copilot" label="Copilot uploads" />
            <Tab value="study" label="Study plans" />
          </Tabs>

          {section === "study" ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                p: 2,
              }}
            >
              <AdminStatsStudyPlans />
            </Paper>
          ) : null}
          {section === "copilot" ? (

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.35)"
                  : "0 12px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            <TableContainer>
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress size={36} />
                </Box>
              ) : (
                <Table size="medium" sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headCellSx, minWidth: 240 }}>
                        User
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 280 }}>
                        Material
                      </TableCell>
                      <TableCell sx={headCellSx}>Source</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Size</TableCell>
                      <TableCell sx={headCellSx}>Credits</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 96 }} align="right">
                        View
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ ...bodyCellSx, py: 8 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                          >
                            No Copilot uploads match this filter.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => {
                        const user = row.user;
                        const fullName =
                          user?.fullName ||
                          [user?.firstName, user?.lastName]
                            .filter(Boolean)
                            .join(" ") ||
                          "Unknown user";
                        return (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{
                              cursor: "pointer",
                              "&:last-child td": { borderBottom: 0 },
                            }}
                            onClick={() =>
                              navigate(`/copilot-usage/${row.id}`)
                            }
                          >
                            <TableCell sx={bodyCellSx}>
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                              >
                                <Avatar
                                  src={user?.profileImage || undefined}
                                  alt={fullName}
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    fontSize: 14,
                                    bgcolor: "primary.main",
                                  }}
                                >
                                  {initialsFor(user)}
                                </Avatar>
                                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    title={fullName}
                                  >
                                    {fullName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    title={user?.email || ""}
                                    sx={{ wordBreak: "break-all" }}
                                  >
                                    {user?.email || "—"}
                                  </Typography>
                                  {user?.role ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        textTransform: "capitalize",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {user.role}
                                    </Typography>
                                  ) : null}
                                </Stack>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, maxWidth: 360 }}>
                              <Stack spacing={0.25}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                  }}
                                  title={row.title || ""}
                                >
                                  {row.title || "Untitled"}
                                </Typography>
                                {row.fileName ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    title={row.fileName}
                                    sx={{ wordBreak: "break-all" }}
                                  >
                                    {row.fileName}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Chip
                                size="small"
                                label={
                                  SOURCE_TYPE_LABEL[row.sourceType] ||
                                  row.sourceType ||
                                  "—"
                                }
                                color={sourceTypeChipColor(row.sourceType)}
                                variant="outlined"
                                sx={{ fontWeight: 600, textTransform: "none" }}
                              />
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Chip
                                size="small"
                                label={
                                  STATUS_LABEL[row.status] || row.status || "—"
                                }
                                color={statusChipColor(row.status)}
                                variant={
                                  row.status === "failed" ? "filled" : "outlined"
                                }
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography variant="body2" color="text.secondary">
                                {formatBytes(row.fileSizeBytes)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography variant="body2" color="text.secondary">
                                {row.creditsCost == null
                                  ? "—"
                                  : `${row.creditsCost}`}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={bodyCellSx}>
                              <Tooltip title="View study pack">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/copilot-usage/${row.id}`);
                                  }}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "primary.light",
                                    borderRadius: 2,
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
            <TablePagination
              component="div"
              count={meta.total}
              page={Math.max(page - 1, 0)}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Uploads per page"
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                px: 2,
                py: 1,
              }}
            />
          </Paper>
          ) : null}
        </Stack>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
